import type React from 'react'
import { useEffect, useState } from 'react'

import { DSInputType, Flex, Icon, Input, Select, SlimButton, Text, Tooltip } from 'components'
import { t } from 'i18next'

import * as S from './styles'

export interface PaginationV2Props {
  currentPage: number
  onPageChange: (page: number) => void
  itemsPerPage: number
  onItemsPerPageChange: (items: number) => void
  totalItems?: number
  itemsPerPageOptions?: number[]
}

export const PaginationV2 = ({
  currentPage,
  onPageChange,
  itemsPerPage,
  onItemsPerPageChange,
  totalItems,
  itemsPerPageOptions = [10, 20, 50, 100],
}: PaginationV2Props) => {
  const [pageInput, setPageInput] = useState(currentPage.toString())
  const totalPages = Math.ceil((totalItems || 0) / itemsPerPage) || 1

  useEffect(() => {
    setPageInput(currentPage.toString())
  }, [currentPage])

  const handleFirstPage = () => {
    if (currentPage > 1) {
      onPageChange(1)
    }
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
    }
  }

  const handleLastPage = () => {
    if (currentPage < totalPages) {
      onPageChange(totalPages)
    }
  }

  const handlePageInputBlur = () => {
    let newPage = parseInt(pageInput, 10)
    if (isNaN(newPage)) {
      setPageInput(currentPage.toString())
      return
    }

    if (newPage < 1) newPage = 1
    if (newPage > totalPages) newPage = totalPages

    if (newPage !== currentPage) {
      onPageChange(newPage)
    } else {
      setPageInput(newPage.toString())
    }
  }

  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handlePageInputBlur()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value)
  }

  const handleItemsPerPageSelect = (value: number) => {
    onItemsPerPageChange(value)
  }

  return (
    <Flex alignItems="center" fullWidth justifyContent="space-between">
      <Flex alignItems="center" gap="sm">
        <S.InputWrapper>
          <Select
            name="items-per-page"
            onChange={(e) => {
              const value = e.target.value
              handleItemsPerPageSelect(Number(value))
            }}
            options={itemsPerPageOptions.map((option) => ({
              title: String(option),
              value: option,
            }))}
            value={itemsPerPage}
          />
        </S.InputWrapper>
        <Text noWrap size="xs">
          {t('common.itemsPerPage')}
        </Text>
      </Flex>

      <Flex alignItems="center" gap="sm">
        <Tooltip arrow placement="top" title={t('common.firstPage')}>
          <S.ButtonWrapper>
            <SlimButton
              disabled={currentPage === 1}
              icon={<Icon icon="chevrons-left" size="18" />}
              onClick={handleFirstPage}
              variant="border"
            />
          </S.ButtonWrapper>
        </Tooltip>

        <Tooltip arrow placement="top" title={t('common.previousPage')}>
          <S.ButtonWrapper>
            <SlimButton
              disabled={currentPage === 1}
              icon={<Icon icon="chevron-left" size="18" />}
              onClick={handlePreviousPage}
              variant="border"
            />
          </S.ButtonWrapper>
        </Tooltip>

        <S.InputWrapper>
          <Input
            design={DSInputType.NUMBER_INPUT}
            onBlur={handlePageInputBlur}
            onChange={handleInputChange}
            onKeyDown={handlePageInputKeyDown}
            type="number"
            value={pageInput}
          />
        </S.InputWrapper>

        <Text noWrap size="xs">
          ... {totalPages}
        </Text>

        <Tooltip arrow placement="top" title={t('common.nextPage')}>
          <S.ButtonWrapper>
            <SlimButton
              disabled={currentPage === totalPages}
              icon={<Icon icon="chevron-right" size="18" />}
              onClick={handleNextPage}
              variant="border"
            />
          </S.ButtonWrapper>
        </Tooltip>

        <Tooltip arrow placement="top" title={t('common.lastPage')}>
          <S.ButtonWrapper>
            <SlimButton
              disabled={currentPage === totalPages}
              icon={<Icon icon="chevrons-right" size="18" />}
              onClick={handleLastPage}
              variant="border"
            />
          </S.ButtonWrapper>
        </Tooltip>
      </Flex>
    </Flex>
  )
}
